import config, { DEFAULT_ERROR_SNACK_SHOW, IS_WEB } from "./config";
import { language, local } from "./internationalization";
import * as StackTrace from "stacktrace-js";
import { showErrorSnackbar } from "./snackbar";
import store from "./store";
import { Logger, getLogger } from "./logging";

// const MUTE_LOGS = !__DEV__;
// const SAVE_LOGS = __DEV__ || __ADMIN__;
// const MUTE_LOGS = false;

const log = getLogger("Crashlytics");

let gErrorHandler = null;

export const stringifyError = (error, exclude = ["stack"]) => {
  const properties = Object.getOwnPropertyNames(error);
  return JSON.stringify(
    error,
    properties.filter((el) => !exclude.includes(el))
  ).replace(/\\"/g, '"');
};

function humanReadableError(errorObj, isConnectivityError) {
  if (isConnectivityError) {
    return "Please check your internet connection 📡";
  }
  //   if (!errorObj || Object.keys(errorObj).length === 0) return null;
  const baseErrorMsg = "An error occured";
  if (errorObj && errorObj.message)
    return `${baseErrorMsg}: ${errorObj.message}`;
  return baseErrorMsg;
}

export const throwErrorWithStack = (
  error,
  showSnackbar = DEFAULT_ERROR_SNACK_SHOW,
  skipConnectivityError = false
) => {
  const isConnectivityError =
    !skipConnectivityError && !store.internetConnected;
  const msg = stringifyError(error) || "<empty message>";
  if (showSnackbar) {
    const humanMsg = humanReadableError(error, isConnectivityError);
    if (humanMsg) showErrorSnackbar(humanMsg, {}, error);
  }
  const newError = new Error(msg, { cause: error });
  newError.isConnectivityError = isConnectivityError;
  throw newError;
};

export const catchErrorWithStack = (
  error,
  showSnackbar = DEFAULT_ERROR_SNACK_SHOW,
  skipConnectivityError = false
) => {
  const msg = stringifyError(error) || "<empty message>";
  if (showSnackbar) {
    const isConnectivityError =
      !skipConnectivityError && !store.internetConnected;
    const humanMsg = humanReadableError(error, isConnectivityError);
    if (humanMsg) showErrorSnackbar(humanMsg, {}, error);
  }
  const newError = new Error(msg, { cause: error });
  newError.stack = error.stack;
  if (gErrorHandler) gErrorHandler({ error: newError });
};

const _cleanStack = (stack) => {
  if (stack.length && stack[0].functionName == "throwErrorWithStack")
    return stack.slice(1, stack.length);
  return stack;
};

const getRecordException = (FirebaseCrashlytics) =>
  async function () {
    log.error(...arguments);
    if (!IS_WEB) await FirebaseCrashlytics.recordException(...arguments);
  };

const setStorageContent = async (FirebaseCrashlytics, App) => {
  if (IS_WEB) return;
  const storeItems = { ...store.getAll() };
  for (const key of Object.keys(storeItems)) {
    if (storeItems[key] === undefined) {
      log.warn(`Skipping undefined value for key ${key} in store`);
      continue;
    }
    await FirebaseCrashlytics.setCustomKey({
      key,
      value: storeItems[key],
      type: "string",
    });
  }
  await FirebaseCrashlytics.setCustomKey({
    key: "language",
    value: language,
    type: "string",
  });
  await FirebaseCrashlytics.setCustomKey({
    key: "local",
    value: local,
    type: "string",
  });
  let build, version;
  const { BUILD, VERSION } = config;
  if (!BUILD || !BUILD) {
    const appInfo = await App.getInfo();
    build = appInfo.build;
    version = appInfo.version;
  } else {
    build = BUILD;
    version = VERSION;
  }
  await FirebaseCrashlytics.setCustomKey({
    key: "build",
    value: build,
    type: "string",
  });
  await FirebaseCrashlytics.setCustomKey({
    key: "version",
    value: version,
    type: "string",
  });
};

export const getHandleException = (FirebaseCrashlytics, App) => {
  const recordException = getRecordException(FirebaseCrashlytics);
  return async (event) => {
    const { error } = event;
    if (!error) {
      log.error("Couldn't determine error", event);
      return;
    }
    if (error.isConnectivityError || local === "ru-RU") {
      log.error(`Not logging error to Crashlytics: ${error.message}`);
      return;
    }
    await setStorageContent(FirebaseCrashlytics, App);
    if (error.stack) {
      let stacktrace = await StackTrace.fromError(error);
      stacktrace = _cleanStack(stacktrace);
      await recordException({
        message: stringifyError(error, ["stack"]),
        stacktrace,
      });
    } else {
      await recordException({
        message: stringifyError(error),
      });
    }
  };
};

export const getHandleUnhandledRejection = (FirebaseCrashlytics, App) => {
  const recordException = getRecordException(FirebaseCrashlytics);
  return async (event) => {
    const { reason, type } = event;
    if (!type) {
      log.error("Couldn't determine error", event);
      return;
    }
    if (reason.isConnectivityError || local === "ru-RU") {
      log.error(`Not logging error to Crashlytics: ${reason.message}`);
      return;
    }
    await setStorageContent(FirebaseCrashlytics, App);
    if (reason.stack) {
      let stacktrace = await StackTrace.fromError(reason);
      stacktrace = _cleanStack(stacktrace);
      await recordException({
        message: `Uncaught (in promise) Error: ${stringifyError(reason, [
          "stack",
        ])}`,
        stacktrace,
      });
    } else {
      await recordException({
        message: `Uncaught (in promise) Error: ${stringifyError(reason)}`,
      });
    }
  };
};

export async function initCrashlytics(FirebaseCrashlytics, Device, App) {
  const errorHandler = getHandleException(FirebaseCrashlytics, App);
  gErrorHandler = errorHandler;
  const unhandledRejectionHandler = getHandleUnhandledRejection(
    FirebaseCrashlytics,
    App
  );
  window.addEventListener("error", errorHandler);
  window.addEventListener("unhandledrejection", unhandledRejectionHandler);

  //   const logLevels = {
  //     debug: log.debug,
  //     log: log.info,
  //     warn: log.warn,
  //     error: log.error,
  //   };

  const loggingHandler = async ({ level, name, args }) => {
    await FirebaseCrashlytics.log({
      message: `[${level.toUpperCase()}] [${name}] ${stringifyConsoleArgs(
        ...args
      )}`,
    });
  };

  if (!IS_WEB) Logger.addHandler(loggingHandler);
  //   Object.entries(logLevels).forEach(([level, originalLog]) => {
  //     console[level] = async function (...args) {
  //       if (!MUTE_LOGS) originalLog.apply(console, args);
  //       if (!IS_WEB)
  //     };
  //   });

  if (IS_WEB) return;

  await FirebaseCrashlytics.setEnabled({ enabled: true });
  const { identifier } = await Device.getId();
  FirebaseCrashlytics.setUserId({
    userId: store.user.uid || identifier,
  });

  log.debug("Initialized");
}

function stringifyConsoleArgs(...args) {
  if (args.length > 1) return args.map((arg) => JSON.stringify(arg)).join(", ");
  if (args.length === 1) return JSON.stringify(args[0]);
}
